import React from "react";
import LegalPage from "@/components/site/LegalPage";

const SECTIONS = [
  {
    id: "agreement",
    heading: "Agreement",
    body: [
      { type: "p", text: "These Terms of Service (the \"Terms\") form a binding agreement between CoSec Global Ltd, a company registered in England and Wales with company number 12345678, registered office 20–22 Wenlock Road, London, N1 7GU (\"CoSec Global\", \"we\", \"us\", \"our\"), and the customer that subscribes to the Services (\"Customer\", \"you\", \"your\")." },
      { type: "p", text: "By creating an account, signing an order form, or using the Services, you agree to be bound by these Terms. If you are accepting these Terms on behalf of a company, you confirm you have authority to do so." },
    ],
  },
  {
    id: "definitions",
    heading: "Definitions",
    body: [
      { type: "list", items: [
        "\"Services\" means CoSec App, CoVerify, and any related applications, websites and APIs operated by CoSec Global.",
        "\"Customer Data\" means data that you or your Authorised Users submit to or generate within the Services, including statutory entity data, officer details, PSC information, board minutes and identity verification records.",
        "\"Authorised User\" means an individual you authorise to access the Services on your behalf.",
        "\"Order Form\" means the document that sets out the plan, term, fees and scope of your subscription.",
      ]},
    ],
  },
  {
    id: "subscription",
    heading: "Subscription, term and fees",
    body: [
      { type: "p", text: "Your subscription is for the term and plan set out in the Order Form (or, if no Order Form has been signed, the plan you selected at sign-up). Subscriptions renew automatically at the end of each term unless cancelled by either party with at least 30 days written notice before the renewal date." },
      { type: "p", text: "Fees are payable in advance, in pounds sterling, by the payment method on file. Fees exclude VAT, which will be added where applicable. We may increase fees at renewal by giving at least 60 days notice. Failure to pay fees when due may result in suspension of the Services." },
    ],
  },
  {
    id: "your-obligations",
    heading: "Your obligations",
    body: [
      { type: "p", text: "You agree to:" },
      { type: "list", items: [
        "Use the Services lawfully and in compliance with these Terms and applicable law (including the Companies Act 2006, the Economic Crime and Corporate Transparency Act 2023, and the Money Laundering Regulations 2017).",
        "Keep your Authorised Users' login credentials confidential, and notify us promptly of any suspected unauthorised access.",
        "Ensure Customer Data you submit is accurate and that you have the rights to submit it.",
        "Not attempt to reverse-engineer, scrape, or interfere with the Services or our infrastructure.",
      ]},
    ],
  },
  {
    id: "customer-data",
    heading: "Customer Data and intellectual property",
    body: [
      { type: "p", text: "As between you and us, you own all Customer Data. You grant us a non-exclusive, worldwide, royalty-free licence to host, process and display Customer Data solely to deliver the Services." },
      { type: "p", text: "We own all rights, title and interest in the Services, including the underlying software, content and trademarks. Nothing in these Terms transfers any of those rights to you." },
      { type: "p", text: "We may use aggregated, anonymised data derived from the Services to improve our products and produce industry benchmarks." },
    ],
  },
  {
    id: "service-levels",
    heading: "Service levels and support",
    body: [
      { type: "p", text: "We target 99.9% uptime per calendar month, measured at the Services' production endpoints. Planned maintenance is scheduled outside UK business hours and announced in advance. Service credits for unscheduled downtime exceeding our target are available on Firm and Enterprise plans, as set out in the Order Form." },
      { type: "p", text: "Support is available by email and in-app chat during UK business hours (09:00–18:00 Mon–Fri). Priority and 24/7 support are available on Firm and Enterprise plans." },
    ],
  },
  {
    id: "warranties",
    heading: "Warranties and disclaimers",
    body: [
      { type: "p", text: "We warrant that the Services will perform substantially in accordance with our documentation, and that we will provide them with reasonable skill and care." },
      { type: "p", text: "Except as expressly set out in these Terms, the Services are provided \"as is\". We disclaim all other warranties to the maximum extent permitted by law, including fitness for a particular purpose, accuracy of third-party data (including identity verification outcomes), and uninterrupted operation." },
    ],
  },
  {
    id: "liability",
    heading: "Limitation of liability",
    body: [
      { type: "p", text: "Nothing in these Terms limits liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be limited by law." },
      { type: "p", text: "Subject to the above, our total liability to you in any 12-month period is limited to the total fees paid by you for the Services in that 12-month period." },
      { type: "p", text: "Neither party is liable to the other for indirect or consequential loss, loss of profit, loss of goodwill, or loss of anticipated savings, whether arising in contract, tort or otherwise." },
    ],
  },
  {
    id: "termination",
    heading: "Suspension and termination",
    body: [
      { type: "p", text: "Either party may terminate the agreement on written notice if the other party (a) commits a material breach that is not remedied within 30 days, or (b) becomes insolvent." },
      { type: "p", text: "On termination, your access to the Services ends. You may export your Customer Data for 30 days after termination, after which it will be deleted (subject to our legal retention obligations)." },
    ],
  },
  {
    id: "governing-law",
    heading: "Governing law and jurisdiction",
    body: [
      { type: "p", text: "These Terms are governed by the laws of England and Wales. Any dispute arising out of or in connection with these Terms is subject to the exclusive jurisdiction of the courts of England and Wales." },
    ],
  },
  {
    id: "contact",
    heading: "Contact us",
    body: [
      { type: "p", text: "Notices under these Terms should be sent to legal@cosecglobal.co or to CoSec Global Ltd, 20–22 Wenlock Road, London, N1 7GU, United Kingdom." },
    ],
  },
];

export default function TermsPage() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Terms of Service"
      updatedAt="08 March 2026"
      sections={SECTIONS}
      related={[
        { href: "/privacy", label: "Privacy Policy" },
        { href: "/cookies", label: "Cookies Policy" },
        { href: "/contact", label: "Contact our legal team" },
      ]}
    />
  );
}
